/// <amd-module name="Wit/Common/PlanningPane/PlanningPane" />
import * as React from "react";
import { IIterationData, ITeamSettings } from "Agile/Contracts/Common";
import { getTFSProject } from "Tfs/Platform/Page";
import { IObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Header } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Link } from "VSSUI/Link";
import { BoltListDragEvent, IListDropData, IListItemDetails, List, ListDropTargetBehavior, ListItem } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { DragDropEffect } from "VSSUI/Utilities/DragDrop";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";
import { HubType, IBacklogTreeRow } from "Wit/Common/BacklogContentService/BacklogContentTreeTypes";
import { IBacklogWorkItemConfigurationService } from "Wit/Common/BacklogContentService/BacklogWorkItemConfigurationService";
import { IBacklogWorkItemService } from "Wit/Common/BacklogContentService/IBacklogWorkItemService";
import { IBacklogsHubContentService } from "Wit/Common/BacklogContentService/IBacklogsHubContentService";
import { ICommonBacklogContentService } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import { IHubContentService } from "Wit/Common/BacklogContentService/IHubContentService";
import { ISprintsHubContentService } from "Wit/Common/BacklogContentService/ISprintsHubContentService";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { PlanningSprintCard } from "Wit/Common/PlanningPane/Components/PlanningSprintCard";
import * as Resources from "Wit/Common/PlanningPane/Resources";
import { IPlanningPaneViewService } from "Wit/Common/PlanningPane/Services/PlanningPaneViewService";
import { SprintEditorDialog } from "Wit/Common/SprintEditorDialog/SprintEditorDialog";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import { FetchWorkItemsDialog } from "Wit/Common/Utils/WorkItemUtils/FetchWorkItemsDialog";
import { restoreSelection } from "Wit/Common/WorkItemDialogs/Components/ProcessWorkItemsDialog";
import { IWorkItemNodeService } from "Wit/Common/WorkItemNodeService/IWorkItemNodeService";

export interface IPlanningPaneProps {
    onDismiss: () => void;
    hub: HubType;
}

export class PlanningPane extends VssComponent<IPlanningPaneProps> {
    private backlogWorkItemConfigurationSvc: IBacklogWorkItemConfigurationService;
    private dropBehavior = new ListDropTargetBehavior<ITreeItemEx<IBacklogTreeRow>>({
        type: "backlog-item",
        id: "backlog-planning-pane",
        onDrop: (event: BoltListDragEvent<HTMLElement, ITreeItemEx<IBacklogTreeRow>>, dropData: IListDropData) => this.onDrop(event, dropData),
        onDragOver: (event: BoltListDragEvent<HTMLElement, ITreeItemEx<IBacklogTreeRow>>, dropData: IListDropData) => this.onDragOver(event, dropData),
        allowedTypes: ["backlog-item", "taskboard-card"],
        isTree: true
    });

    private planningPaneViewSvc: IPlanningPaneViewService;
    private projectId: string;
    private teamSettings: IObservableValue<ITeamSettings | undefined>;
    private teamSettingsSvc: ITeamSettingsService;
    private commonBacklogContentService: ICommonBacklogContentService;
    private backlogWorkItemService: IBacklogWorkItemService;
    private useChangeIterationPerfomanceOptimization: boolean;
    private hubService: IHubContentService;

    constructor(props: IPlanningPaneProps, context: IVssComponentContext) {
        super(props, context);
        this.projectId = getTFSProject(context.pageContext)?.id ?? "";
        this.backlogWorkItemConfigurationSvc = context.pageContext.getService<IBacklogWorkItemConfigurationService>(
            "IBacklogWorkItemConfigurationService"
        );
        this.planningPaneViewSvc = context.pageContext.getService<IPlanningPaneViewService>("IPlanningPaneViewService");
        this.teamSettingsSvc = context.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
        this.teamSettings = this.teamSettingsSvc.getTeamSettingsObservable();
        this.commonBacklogContentService = context.pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
        this.backlogWorkItemService = context.pageContext.getService<IBacklogWorkItemService>("IBacklogWorkItemService");

        // Load icon asynchronously data if it hasn't already been loaded
        this.backlogWorkItemConfigurationSvc.getWorkItemTypeColorAndIcons();
        this.useChangeIterationPerfomanceOptimization = isFeatureFlagEnabled(
            context.pageContext,
            FeatureFlags.UseChangeIterationPerfomanceOptimization,
            false
        );

        if (this.useChangeIterationPerfomanceOptimization) {
            this.hubService = this.getHubService(context.pageContext, props.hub);
        }
    }

    private getHubService = (pageContext: IVssPageContext, hub: HubType): IHubContentService => {
        return hub === HubType.Backlogs
            ? pageContext.getService<IBacklogsHubContentService>("IBacklogsHubContentService")
            : pageContext.getService<ISprintsHubContentService>("ISprintsHubContentService");
    }

    public render(): JSX.Element {
        return (
            <React.Fragment>
                <div className="full-height v-scroll-auto padding-right-8" data-elementtype="side-panel-container">
                    <Header
                        className="planning-pane-header"
                        title={Resources.PlanningHeader}
                        description={Resources.PlanningHeaderDescription}
                        commandBarItems={this.getHeaderCommandBarItems()}
                    />
                    <Observer teamSettings={this.teamSettingsSvc.getTeamSettingsObservable()}>
                        {(observedProps: { teamSettings: ITeamSettings | undefined }) => {
                            const teamSettings = observedProps?.teamSettings;
                            let currentiterationIndex = -1;
                            if (teamSettings) {
                                // Get iterations with count cap
                                const items: (string | IIterationData)[] = this.planningPaneViewSvc.getIterations();
                                if (teamSettings.teamName) {
                                    items.unshift(teamSettings.teamName);
                                }
                                this.planningPaneViewSvc.planningItemProvider = new ArrayItemProvider(items);
                                return (
                                    <React.Fragment>
                                        <div className="padding-left-16 padding-right-8 flex-grow planning-list">
                                            {items.length > 0 && (
                                                <List
                                                    behaviors={[this.dropBehavior]}
                                                    className="flex-card full-width"
                                                    itemProvider={this.planningPaneViewSvc.planningItemProvider}
                                                    overlay=".planning-sprint-card"
                                                    renderRow={(
                                                        rowIndex: number,
                                                        item: IIterationData | string,
                                                        details: IListItemDetails<IIterationData | string>
                                                    ) => {
                                                        return (
                                                            <ListItem key={rowIndex} index={rowIndex} details={details}>
                                                                {typeof item === "string" ? (
                                                                    <Card className="planning-sprint-card flex-grow">
                                                                        <div data-iterationpath={teamSettings.teamFieldDefaultValue}>
                                                                            {this.props.hub !== HubType.Backlogs ? (
                                                                                <FPSLink href={this.planningPaneViewSvc.getBacklogUrl(item)}>
                                                                                    {format(Resources.TeamBacklogLink, teamSettings.teamName)}
                                                                                </FPSLink>
                                                                            ) : (
                                                                                <span>
                                                                                    {format(Resources.TeamBacklogLink, teamSettings.teamName)}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </Card>
                                                                ) : (
                                                                    <PlanningSprintCard
                                                                        iteration={item}
                                                                        iterationDetails={this.planningPaneViewSvc.getIterationDetails(
                                                                            teamSettings.teamId || "",
                                                                            item
                                                                        )}
                                                                        weekends={teamSettings.weekends?.days ? teamSettings.weekends.days : []}
                                                                        workItemTypeColorAndIcons={this.backlogWorkItemConfigurationSvc.getObservableWorkItemTypeColorAndIcons()}
                                                                        iterationUrl={
                                                                            teamSettings.teamName
                                                                                ? this.planningPaneViewSvc.getIterationUrl(
                                                                                    teamSettings.teamName,
                                                                                    item
                                                                                )
                                                                                : undefined
                                                                        }
                                                                        key={item.id}
                                                                        current={rowIndex === currentiterationIndex}
                                                                        rowIndex={rowIndex}
                                                                    />
                                                                )}
                                                            </ListItem>
                                                        );
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <div className="padding-left-16 padding-top-16 padding-bottom-16 padding-right-8 flex-row justify-end">
                                            <Button
                                                iconProps={{ iconName: "Add" }}
                                                text={Resources.NewSprint}
                                                onClick={() => this.openNewSprintDialog()}
                                            />
                                        </div>
                                    </React.Fragment>
                                );
                            } else {
                                return (
                                    <ZeroData
                                        imagePath={getAssetLocation(
                                            this.context.pageContext,
                                            "ms.vss-work-web/common-planning-pane/Content/zerodata-get-started-with-sprint.svg"
                                        )}
                                        imageAltText={Resources.ZeroDataPrimaryText}
                                        primaryText={Resources.ZeroDataPrimaryText}
                                        secondaryText={this.renderZeroDataSecondaryText()}
                                        actionText={Resources.NewSprint}
                                        actionType={ZeroDataActionType.ctaButton}
                                        onActionClick={() => this.openNewSprintDialog()}
                                    />
                                );
                            }
                        }}
                    </Observer>
                </div>
            </React.Fragment>
        );
    }

    private onDragOver = (event: BoltListDragEvent<HTMLElement, ITreeItemEx<IBacklogTreeRow>>, dropData: IListDropData): void => {
        const row = event.detail.dataTransfer.data.underlyingItem.data.value;
        const teamOwnsWorkItem = this.backlogWorkItemService.isBacklogWorkItemReorderableAtAreaPath(
            row.coreColumnMap[this.commonBacklogContentService.getTeamField() ?? CoreFieldRefNames.AreaPath] as string
        );
        if (!teamOwnsWorkItem) {
            event.detail.dataTransfer.dropEffect = DragDropEffect.none;
        }
    };

    private onDrop = async (event: BoltListDragEvent<HTMLElement, ITreeItemEx<IBacklogTreeRow>>, dropData: IListDropData): Promise<void> => {
        const workItem: IBacklogTreeRow = event.detail.dataTransfer.data?.underlyingItem?.data;
        if (this.useChangeIterationPerfomanceOptimization) {
            await this.fetchSelectedItems();
        }

        const index = dropData.index;
        const currentIterationData = this.planningPaneViewSvc.getIterationInfo(index);

        const selectedTreeItems = this.useChangeIterationPerfomanceOptimization 
            ? await this.commonBacklogContentService.getSelectedProviderItemsWithChildren() 
            : [];

        await this.backlogWorkItemService.updateIteration(
            currentIterationData,
            workItem,
            async (workItemDTO, numberOfSavedWorkitems) => {
                if (this.useChangeIterationPerfomanceOptimization) {
                    await this.hubService.onIterationChange(selectedTreeItems);
                }
                this.planningPaneViewSvc.updateIterationPanel(index, workItemDTO, numberOfSavedWorkitems);
            },
            this.useChangeIterationPerfomanceOptimization,
            this.props.hub === HubType.Sprints);
    };

    private getHeaderCommandBarItems(): IHeaderCommandBarItem[] {
        return [
            {
                ariaLabel: Resources.Close,
                iconProps: { iconName: "Clear" },
                id: `mapping-pane-close-button`,
                role: "button",
                onActivate: this.props.onDismiss,
                subtle: true
            }
        ];
    }

    private renderZeroDataSecondaryText(): JSX.Element {
        return (
            <React.Fragment>
                <div>{Resources.ZeroDataSecondaryText}</div>
                <Link href="https://go.microsoft.com/fwlink/?linkid=617156" target="_blank">
                    {Resources.LearnMore}
                </Link>
            </React.Fragment>
        );
    }

    private fetchSelectedItems = async (): Promise<void> => {
        const selection = this.commonBacklogContentService.getTreeSelection();
        const notFetchedWorkItemIds = await this.commonBacklogContentService.getSelectedItemsIdFromRawBacklogTree(true, this.useChangeIterationPerfomanceOptimization);
        const selectionCopy = selection.value.map(item => ({ ...item }));
        await new Promise(resolve => {
            this.openDialog(onDismiss => (
                <FetchWorkItemsDialog
                    onDismiss={() => {
                        onDismiss();
                        const selectedItems = this.commonBacklogContentService.getSelectedItems().map(item => ({
                            value: item,
                            id: item.value.coreColumnMap[CoreFieldRefNames.Id],
                            teamProject: item.value.coreColumnMap[CoreFieldRefNames.TeamProject] as string,
                            ...item.value.coreColumnMap
                        }));
                        resolve(selectedItems);
                    }}
                    processWorkItems={async workItemIds => {
                        await this.backlogWorkItemService.fetchWorkItems(workItemIds);
                        return true;
                    }}
                    workItemIds={notFetchedWorkItemIds}
                    callBack={() => {
                        if (this.useChangeIterationPerfomanceOptimization) {
                            restoreSelection(selectionCopy, selection);
                        } else {
                            for (const range of selectionCopy) {
                                selection.select(range.beginIndex, range.endIndex - range.beginIndex + 1);
                            }
                        }
                    }}
                />
            ));
        });
    };

    private onSprintCreated = (): void => {
        this.teamSettings = this.teamSettingsSvc.getTeamSettingsObservable({ ignoreCache: true });
        const nodeService = this.context.pageContext.getService<IWorkItemNodeService>("IWorkItemNodeService");
        nodeService.removeIterationNodes(this.projectId);
    };

    private openDialog(callout: (onDismiss: () => void) => JSX.Element): void {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(callout);
    }

    private openNewSprintDialog = () => {
        this.openDialog(onDismiss => {
            return (
                <SprintEditorDialog
                    onComplete={this.onSprintCreated}
                    onDismiss={onDismiss}
                    showTeamDropdown={false}
                    teamSettings={this.teamSettings}
                />
            );
        });
    };
}
